<script setup lang="ts">
export type NavLink = {
  type?: 'divider' | 'subheader' | 'skeleton'
  title?: string
  subtitle?: string
  value?: string
  to?: string
  href?: string
  target?: string
  prependIcon?: string
  appendIcon?: string
  appendIconSize?: string
  appendIconColor?: string
  appendIconTooltip?: string
  color?: string
  disabled?: boolean
  active?: boolean
  /** Low emphasis for this item and everything under it. */
  muted?: boolean
  /** Short context after the title, like the year a group's links point at. */
  badge?: string
  children?: NavLink[]
}

const props = defineProps<{
  item: NavLink
  rail: boolean
  /** Set by a muted parent group. */
  muted?: boolean
}>()

const isMuted = computed(() => props.muted || !!props.item.muted)
</script>

<template>
  <v-skeleton-loader
    v-if="item.type === 'skeleton'"
    class="bg-transparent"
    type="list-item, list-item"
  />
  <v-divider v-else-if="item.type === 'divider' || (item.type === 'subheader' && rail)" class="my-2" />
  <v-list-subheader v-else-if="item.type === 'subheader'" class="nav-subheader">
    {{ item.title }}
  </v-list-subheader>
  <v-list-group v-else-if="item.children" :value="item.value">
    <template #activator="{ props: activatorProps }">
      <v-tooltip :disabled="!rail" :text="item.title" location="end">
        <template #activator="{ props: tooltipProps }">
          <v-list-item
            v-bind="{ ...activatorProps, ...tooltipProps }"
            :title="item.title"
            :subtitle="rail ? undefined : item.subtitle"
            :prepend-icon="item.prependIcon"
            :color="item.color"
            :class="{ 'nav-muted': isMuted }"
          >
            <template v-if="item.badge" #title>
              {{ item.title }}<span class="nav-badge">{{ item.badge }}</span>
            </template>
          </v-list-item>
        </template>
      </v-tooltip>
    </template>
    <NavItem
      v-for="(child, i) in item.children"
      :key="i"
      :item="child"
      :rail="rail"
      :muted="isMuted"
    />
  </v-list-group>
  <v-tooltip v-else :disabled="!rail" :text="item.title" location="end">
    <template #activator="{ props: tooltipProps }">
      <v-list-item
        v-bind="tooltipProps"
        :title="item.title"
        :subtitle="rail ? undefined : item.subtitle"
        :to="item.to"
        :href="item.href"
        :target="item.target"
        :prepend-icon="item.prependIcon"
        :color="item.color"
        :disabled="item.disabled"
        :active="item.active"
        :class="{ 'nav-muted': isMuted }"
      >
        <template v-if="item.badge" #title>
          {{ item.title }}<span class="nav-badge">{{ item.badge }}</span>
        </template>
        <template v-if="item.appendIcon" #append>
          <v-tooltip :disabled="!item.appendIconTooltip" :text="item.appendIconTooltip" location="end">
            <template #activator="{ props: iconProps }">
              <v-icon
                v-bind="iconProps"
                :icon="item.appendIcon"
                :color="item.appendIconColor"
                :size="item.appendIconSize"
              />
            </template>
          </v-tooltip>
        </template>
      </v-list-item>
    </template>
  </v-tooltip>
</template>

<style scoped>
.nav-subheader {
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.nav-badge {
  margin-left: 6px;
  font-size: 0.7rem;
  font-variant-numeric: tabular-nums;
  opacity: 0.6;
}

/* Still reachable, but faded back until hovered or open. */
.nav-muted {
  opacity: 0.6;
  transition: opacity 120ms;
}

.nav-muted.v-list-item--active,
.nav-muted:hover,
.nav-muted:focus-visible {
  opacity: 1;
}

.nav-muted :deep(.v-list-item-title) {
  font-size: 0.8125rem;
}
</style>
